import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsCommands} from "../../shared/dto/aps.commands";
import * as angular from "angular";
import * as _ from "lodash";
import HttpServiceName, {HttpService} from "../http/http.service";
import {Endpoints} from "../../shared/config/constant.module";

let requires = [HttpServiceName];

export interface LineConfigsService {
  getLineConfiguration(shopId: string): angular.IPromise<apsViewModels.ShopInspectionLines>;
  addLine(line: apsCommands.AddLine): angular.IPromise<any>;
  updateLine(line: apsCommands.UpdateLineName): angular.IPromise<any>;
  addLineEquipment(line: apsCommands.AddLineEquipment): angular.IPromise<any>;
  updateLineEquipment(line: apsCommands.UpdateLineEquipment): angular.IPromise<any>;
  addLineJunction(line: apsCommands.AddLineJunction): angular.IPromise<any>;
  updateLineJunction(line: apsCommands.UpdateLineJunction): angular.IPromise<any>;
  removeLine(line: apsCommands.RemoveLine): angular.IPromise<any>;
  removeLineEquipment(lineEquipment: apsCommands.RemoveLineEquipment): angular.IPromise<any>;
  removeLineJunction(lineJunction: apsCommands.RemoveLineJunction): angular.IPromise<any>;
  getLineEquipmentNames(): angular.IPromise<apsViewModels.LineEquipmentNameItem[]>;
  toggleLine(line: apsCommands.ToggleLine): angular.IPromise<any>;
  toggleLineEquipment(lineEquipment: apsCommands.ToggleLineEquipment): angular.IPromise<any>;
  addLineEquipmentName(lineEquipmentName: apsCommands.AddLineEquipmentName): angular.IPromise<any>;
  updateLineEquipmentName(lineEquipmentName: apsCommands.UpdateLineEquipmentName): angular.IPromise<any>;
  removeLineEquipmentName(lineEquipmentName: apsCommands.RemoveLineEquipmentName): angular.IPromise<any>;
  reorderLineEquipment(lineEquipment: apsCommands.ReorderLineEquipment): angular.IPromise<any>;
  updateEquipment(line: LineItem): void;
}

export interface LineCounts extends apsViewModels.ShopInspectionLines {
  lines: LineItem[];
  junctions: LineJunctionItem[];
  equipment: {[id: string]: LineEquipmentItem};
}

export interface LineItem extends apsViewModels.LineItem {
  equipment: LineEquipmentItem[];
  startJunction: LineJunctionItem;
  endJunction: LineJunctionItem;
}

export interface LineJunctionItem extends apsViewModels.LineJunctionItem {
  inputs: LineItem[];
  outputs: LineItem[];
}

export interface LineEquipmentItem extends apsViewModels.LineEquipmentItem {
  canMoveLeft: boolean;
  canMoveRight: boolean;
}

export default angular
  .module('aps.services.lineConfigs', requires)
  .service('lineConfigsService', (httpService: HttpService): LineConfigsService => {

    let getLines = (shopId: string): angular.IPromise<apsViewModels.ShopInspectionLines> => {
      return httpService.get<apsViewModels.ShopInspectionLines>(Endpoints.LINE_CONFIG, {
        shopId: shopId
      });
    };

    let setEquipment = (equipment: LineEquipmentItem, line: LineItem): void => {
      equipment.canMoveLeft = equipment.order > _.min(_.map(line.equipment, 'order'));
      equipment.canMoveRight = equipment.order < _.max(_.map(line.equipment, 'order'));
    };

    let updateEquipment = (line: LineItem): void => {
      line.equipment = _.sortBy(line.equipment, 'order');
      line.equipment.forEach(equipment => {
        setEquipment(equipment, line);
      });
    };

    let populateEquipment = (lines: LineItem[]): {[id: string]: LineEquipmentItem} => {

      let equipments: {[id: string]: LineEquipmentItem} = {};
      lines.forEach(line => {
        line.equipment = _.sortBy(line.equipment, 'order');
        line.equipment.forEach(equipment => {
          setEquipment(equipment, line);
          equipments[equipment.id] = equipment;
        });
      });
      return equipments;
    };

    let populateLineJunctions = (shopLines: apsViewModels.ShopInspectionLines): LineCounts => {
      let lines = <LineItem[]>shopLines.lines;
      let junctions = <LineJunctionItem[]>shopLines.junctions;
      junctions.forEach(junction => {
        junction.inputs = lines.filter(line => line.endJunctionId === junction.id);
        junction.inputs.forEach(line => line.endJunction = junction);

        junction.outputs = lines.filter(line => line.startJunctionId === junction.id);
        junction.outputs.forEach(line => line.startJunction = junction);
      });

      let equipment = populateEquipment(lines);

      return {
        lines: lines,
        junctions: junctions,
        equipment: equipment,
        wellKnownMeasures: shopLines.wellKnownMeasures,
        inconsistentEquipmentConfigurations: shopLines.inconsistentEquipmentConfigurations
      };
    };

    let getLineConfiguration = (shopId: string): angular.IPromise<LineCounts> => {
      return getLines(shopId).then(lines => {
        return populateLineJunctions(lines);
      });
    };

    let addLine = (line: apsCommands.AddLine): angular.IPromise<any> => {
      return httpService.post(Endpoints.LINE_CONFIG, line);
    };

    let updateLine = (line: apsCommands.UpdateLineName): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG, line);
    };

    let addLineEquipment = (lineEquipment: apsCommands.AddLineEquipment): angular.IPromise<any> => {
      return httpService.post(Endpoints.LINE_CONFIG_EQUIPMENT, lineEquipment);
    };

    let updateLineEquipment = (lineEquipment: apsCommands.UpdateLineEquipment): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG_EQUIPMENT, lineEquipment);
    };

    let addLineJunction = (lineJunction: apsCommands.AddLineJunction): angular.IPromise<any> => {
      return httpService.post(Endpoints.LINE_CONFIG_JUNCTION, lineJunction);
    };

    let updateLineJunction = (lineJunction: apsCommands.UpdateLineJunction): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG_JUNCTION, lineJunction);
    };

    let removeLine = (line: apsCommands.RemoveLine): angular.IPromise<any> => {
      return httpService.delete(Endpoints.LINE_CONFIG + '/' + line.id, { shopId: line.shopId });
    };

    let removeLineEquipment = (lineEquipment: apsCommands.RemoveLineEquipment): angular.IPromise<any> => {
      return httpService.delete(Endpoints.LINE_CONFIG_EQUIPMENT + '/' + lineEquipment.id,
        { shopId: lineEquipment.shopId });
    };

    let removeLineJunction = (lineJunction: apsCommands.RemoveLineJunction): angular.IPromise<any> => {
      return httpService.delete(Endpoints.LINE_CONFIG_JUNCTION + '/' + lineJunction.id,
        { shopId: lineJunction.shopId });
    };

    let getLineEquipmentNames = (): angular.IPromise<apsViewModels.LineEquipmentNameItem[]> => {
      return httpService.get(Endpoints.LINE_CONFIG_EQUIPMENT_NAME);
    };

    let toggleLine = (line: apsCommands.ToggleLine): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG_TOGGLE, line);
    };

    let toggleLineEquipment = (lineEquipment: apsCommands.ToggleLineEquipment): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG_EQUIPMENT_TOGGLE, lineEquipment);
    };

    let addLineEquipmentName = (lineEquipmentName: apsCommands.AddLineEquipmentName): angular.IPromise<any> => {
      return httpService.post(Endpoints.LINE_EQUIPMENT_NAME, lineEquipmentName);
    };

    let updateLineEquipmentName = (lineEquipmentName: apsCommands.UpdateLineEquipmentName
      ): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_EQUIPMENT_NAME, lineEquipmentName);
    };

    let removeLineEquipmentName = (lineEquipmentName: apsCommands.RemoveLineEquipmentName
      ): angular.IPromise<any> => {
      return httpService.delete(Endpoints.LINE_EQUIPMENT_NAME + '/' + lineEquipmentName.id);
    };

    let reorderLineEquipment = (lineEquipment: apsCommands.ReorderLineEquipment): angular.IPromise<any> => {
      return httpService.put(Endpoints.LINE_CONFIG_EQUIPMENT_REORDER, lineEquipment);
    };

    return {
      getLineConfiguration: getLineConfiguration,
      addLine: addLine,
      updateLine: updateLine,
      addLineEquipment: addLineEquipment,
      updateLineEquipment: updateLineEquipment,
      addLineJunction: addLineJunction,
      updateLineJunction: updateLineJunction,
      removeLine: removeLine,
      removeLineEquipment: removeLineEquipment,
      removeLineJunction: removeLineJunction,
      getLineEquipmentNames: getLineEquipmentNames,
      toggleLine: toggleLine,
      toggleLineEquipment: toggleLineEquipment,
      addLineEquipmentName: addLineEquipmentName,
      updateLineEquipmentName: updateLineEquipmentName,
      removeLineEquipmentName: removeLineEquipmentName,
      reorderLineEquipment: reorderLineEquipment,
      updateEquipment: updateEquipment
    };
  })
  .name;


// WEBPACK FOOTER //
// ./src/app/services/lineConfigs/lineConfigs.service.ts